import { makeAxiosClient } from 'lib/api/ssapi/secondStepClient'
import { domains, isCurriculumDraftMode } from 'config/env'
import { formatError } from 'utils/formatError'
import { K5_PROGRAM_TYPE } from 'utils/productMaps'
import { actions } from './reducer'
import { createCurriculumContentStateKey } from './selectors'

const getClient = () => makeAxiosClient(domains.CURRICULUM_SERVICE)

const getAlternateElementaryDigitalWellBeingRoutePath = ({
  routePath,
  contentGroupCode,
}) => {
  if (!routePath || contentGroupCode !== K5_PROGRAM_TYPE) return null

  const digitalWellBeingSegment = '/lessons/digital-well-being/'
  if (!routePath.includes(digitalWellBeingSegment)) return null

  return routePath.replace(digitalWellBeingSegment, '/lessons/')
}

const isAudiencedRouteNotFoundError = error => {
  return (
    error?.response?.status === 404 &&
    error?.response?.data?.code === 'audienced_content_error'
  )
}

const getAlternateRoutePaths = ({ routePath, contentGroupCode }) => {
  const alternates = [
    getAlternateElementaryDigitalWellBeingRoutePath({
      routePath,
      contentGroupCode,
    }),
  ].filter(Boolean)

  return alternates.filter(
    (alternateRoutePath, index) =>
      alternateRoutePath !== routePath &&
      alternates.indexOf(alternateRoutePath) === index,
  )
}

/**
 * Fetch content from the curriculum service GET /v1/content endpoint.
 * This single call replaces both the route-service and content-service calls.
 *
 * Curriculum service expects query string params (not a JSON body) and
 * authenticates via the `Authorization: Bearer <jwt>` header attached by
 * `makeAxiosClient`.
 */
const fetchCurriculumContent = ({
  contentGroupCode,
  routePath,
  siteId,
  contentGroupVersionId,
}) => {
  return async dispatch => {
    const contentStateKey = createCurriculumContentStateKey(
      contentGroupCode,
      routePath,
      siteId,
    )
    dispatch(actions.fetchingContent({ contentStateKey }))

    try {
      const client = getClient()
      // Send exactly one of contentGroupVersionId / contentGroupCode (the
      // service rejects requests that include more than one scope field).
      const params = {
        routePath,
        siteId,
        ...(contentGroupVersionId
          ? { contentGroupVersionId }
          : { contentGroupCode }),
        ...(isCurriculumDraftMode() ? { draftMode: 'true' } : {}),
      }

      let response
      try {
        response = await client.get('/v1/content', { params })
      } catch (error) {
        if (!isAudiencedRouteNotFoundError(error)) {
          throw error
        }

        const alternateRoutePaths = getAlternateRoutePaths({
          routePath,
          contentGroupCode,
        })
        if (!alternateRoutePaths.length) {
          throw error
        }

        let retryError = error
        for (const alternateRoutePath of alternateRoutePaths) {
          try {
            response = await client.get('/v1/content', {
              params: {
                ...params,
                routePath: alternateRoutePath,
              },
            })
            break
          } catch (innerError) {
            retryError = innerError
          }
        }

        if (!response) {
          throw retryError
        }
      }
      dispatch(
        actions.fetchContentSuccess({
          contentStateKey,
          data: response.data,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchContentError({
          contentStateKey,
          error: formatError(error),
        }),
      )
    }
  }
}

const clearErrors = () => {
  return async dispatch => {
    dispatch(actions.clearErrors())
  }
}

export default {
  fetchCurriculumContent,
  clearErrors,
}
